/**
 * Regla única de cantidades por unidad de medida. Copias idénticas en inventory-svc, admin-web y store-web: cámbialas juntas.
 *
 * La unidad del producto (`UND`, `KG`, `LTS`, `CAJA`, `PAR`, `METRO`) decide cuántos decimales admite su cantidad. El SRI permite hasta
 * 6 decimales (ficha v2.32); estos son los topes de FAISEN. El servidor valida; la pantalla usa la misma regla para el paso y el mensaje.
 */

/** Decimales que admite cada unidad. Una unidad desconocida se trata como entera (lo más estricto). */
export const UNIT_DECIMALS: Readonly<Record<string, number>> = { UND: 0, CAJA: 0, PAR: 0, KG: 3, LTS: 3, METRO: 2 };

/** Las existencias y los saldos se guardan redondeados a 6 decimales: así 1.125 − 0.125 − 1 es exactamente 0 y no 2.2e-16. */
export const QTY_SCALE = 6;

/** Tolerancia al comparar existencias: por debajo de una millonésima es cero. */
export const QTY_EPS = 1e-6;

export function decimalsOf(unit?: string | null): number {
  return UNIT_DECIMALS[String(unit || 'UND').toUpperCase()] ?? 0;
}

/** Redondeo estable de una cantidad (por defecto a 6 decimales). */
export function roundQty(n: number, decimals = QTY_SCALE): number {
  const f = 10 ** decimals;
  return Math.round((Number(n) + Number.EPSILON) * f) / f;
}

export const addQty = (a: number, b: number): number => roundQty(Number(a) + Number(b));
export const subQty = (a: number, b: number): number => roundQty(Number(a) - Number(b));

/** Paso mínimo de la unidad: 1, 0.01 o 0.001. */
export function stepOf(unit?: string | null): number {
  const d = decimalsOf(unit);
  return d === 0 ? 1 : Number((10 ** -d).toFixed(d));
}

/** Cuánto suman o restan los botones +/− de un selector: 1 en unidades enteras y 0.5 en las que admiten decimales. */
export function buttonStepOf(unit?: string | null): number {
  return decimalsOf(unit) === 0 ? 1 : 0.5;
}

/** ¿La cantidad es válida para la unidad? Positiva, con no más decimales de los que admite y al menos el paso mínimo. */
export function isValidQty(unit: string | null | undefined, qty: number): boolean {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return false;
  const d = decimalsOf(unit);
  return roundQty(n, d) === roundQty(n, QTY_SCALE) && n >= stepOf(unit) - 1e-9;
}

/** Mensaje de por qué una cantidad no vale para la unidad, o null si vale. */
export function qtyError(unit: string | null | undefined, qty: number, label = 'La cantidad'): string | null {
  if (isValidQty(unit, qty)) return null;
  const u = String(unit || 'UND').toUpperCase();
  const d = decimalsOf(unit);
  if (!Number.isFinite(Number(qty)) || Number(qty) <= 0) return `${label} debe ser mayor a 0.`;
  return d === 0
    ? `${label} debe ser un número entero (la unidad ${u} no admite decimales).`
    : `${label} admite hasta ${d} decimal${d === 1 ? '' : 'es'} para la unidad ${u} (mínimo ${stepOf(unit)}).`;
}
